import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MIBUILD_PAGE_HEADER_HEIGHT } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { MiLoader } from "../MiLoader/MiLoader";
import { MiSearchInput } from "../MiSearchInput/MiSearchInput";

/**
 * A reusable page header component that displays a title with optional icon, search functionality, and action components.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.title] - The main title text to display
 * @param {string} [props.titleVariant="h5"] - Typography variant for the title
 * @param {React.ReactNode} [props.titleIcon] - Icon to display next to the title
 * @param {string|number} [props.titleFontSize] - Font size for the title (defaults to theme.fontSize.xxlarge)
 * @param {string} [props.titleColor="grey.dark"] - Color for the title text
 * @param {string} [props.titleFontWeight="bold"] - Font weight for the title
 * @param {React.ReactNode} [props.leftComponent] - Component to render in the left section after the title
 * @param {React.ReactNode} [props.centerComponent] - Component to render in the center section
 * @param {React.ReactNode} [props.actionComponent] - Component to render in the action section
 * @param {string} [props.search] - Current search value for the search input
 * @param {Function} [props.onHandleSearch] - Callback function for search input changes
 * @param {string} [props.searchPlaceholder="Search"] - Placeholder text for the search input
 * @param {React.ReactNode} [props.buttonGroupComponent] - Button group component to render in actions
 * @param {Object} [props.style] - Custom styles for the main container
 * @param {Object} [props.centerContainerStyle] - Custom styles for the center container
 * @param {Object} [props.actionContainerStyle] - Custom styles for the action container
 * @param {Object} [props.titleContainerStyle] - Custom styles for the title container
 * @param {boolean} [props.loading=false] - Whether to show the loading indicator
 * @param {boolean} [props.hasDivider=true] - Whether to show a divider at the bottom
 *
 * @returns {React.ReactElement} The rendered page header component
 */
const MiBuildPageHeaderComponent = ({
  title,
  titleVariant = "h5",
  titleIcon,
  titleFontSize,
  titleColor = "text.primary",
  titleFontWeight = "bold",
  leftComponent,
  centerComponent,
  actionComponent,
  search,
  onHandleSearch,
  searchPlaceholder = "Search",
  buttonGroupComponent,
  style,
  centerContainerStyle,
  actionContainerStyle,
  titleContainerStyle,
  loading = false,
  hasDivider = true,
}) => {
  const { classes, theme } = useStyles({ hasDivider });

  return (
    <Box
      id="mipageheader"
      data-test-id
      className={classes.container}
      style={style}
    >
      <Box className={classes.mainContent}>
        <Box className={classes.leftSectionContainer}>
          {titleIcon}
          <Box style={titleContainerStyle}>
            <Typography
              fontSize={titleFontSize || theme.fontSize.xxlarge}
              variant={titleVariant}
              color={titleColor}
              style={{ fontWeight: titleFontWeight, textAlign: "left" }}
            >
              {title || "--"}
            </Typography>
          </Box>
          <Box>{leftComponent}</Box>
        </Box>
        {centerComponent && (
          <Box
            className={classes.centerSectionContainer}
            style={centerContainerStyle}
          >
            {centerComponent}
          </Box>
        )}
        {(buttonGroupComponent || onHandleSearch || actionComponent) && (
          <Box className={classes.actionContainer} style={actionContainerStyle}>
            {buttonGroupComponent && <Box>{buttonGroupComponent}</Box>}
            {onHandleSearch && (
              <MiSearchInput
                value={search}
                handleChange={onHandleSearch}
                placeholder={searchPlaceholder}
                className={classes.searchField}
              />
            )}
            {actionComponent && (
              <Box style={actionContainerStyle}>{actionComponent}</Box>
            )}
          </Box>
        )}
      </Box>
      <MiLoader isLoading={loading} type="linear" data-testid="mi-loader" />
    </Box>
  );
};

export const MiBuildPageHeader = memo(MiBuildPageHeaderComponent);

const useStyles = makeStyles()((theme, { hasDivider }) => ({
  container: {
    backgroundColor: theme.palette.common.white,
    position: "sticky",
    top: 0,
    zIndex: 1000,
    height: MIBUILD_PAGE_HEADER_HEIGHT,
    borderBottom: hasDivider ? `1px solid ${theme.palette.divider}` : null,
  },
  mainContent: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    // the reason its 1.25 is to accomodate the 5px loader
    padding: theme.spacing(1.25, 2, 0, 2),
    height: "47px",
  },
  leftSectionContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    flex: 2,
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  searchField: {
    width: 250,
  },
  centerSectionContainer: {
    display: "flex",
    flexDirection: "row",
    flex: 2,
    alignItems: "center",
    justifyContent: "flex-start",
    [theme.breakpoints.down("lg")]: {
      justifyContent: "flex-end",
    },
    [theme.breakpoints.down("md")]: {
      justifyContent: "flex-end",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    gap: 16,
    paddingRight: 4,
    alignItems: "center",
    justifyContent: "flex-end",
    [theme.breakpoints.down("md")]: {
      gap: 8,
      flexWrap: "wrap",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      justifyContent: "space-around",
      gap: 4,
    },
  },
}));
